// @ts-nocheck
/* eslint-disable spire/export-styles */
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { extractStylesToArray, isSafe } from "@insite/client-framework/Common/Utilities/isSafeStyles";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import * as cssLinter from "css";
import parse from "html-react-parser";
import * as React from "react";
import styled, { css } from "styled-components";

/* Valtech Custom Code - Start */
import getCssUnitField from "@store/Framework/Common/Fields/Toolkit/cssUnit";
import { widgetWrapperStyles } from "@store/Library/Components/Toolkit/WidgetWrapper";
import breakpointValues, { BreakpointValue } from "@toolkit/Framework/Common/Utilities/breakpointValues";
/* Valtech Custom Code - End */

/* Store Custom Code - Start */
import getColor from "@insite/mobius/utilities/getColor";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { spacingStyles } from "@toolkit/Themes/common/mixins";
/* Store Custom Code - End */

const enum fields {
    backgroundColor = "backgroundColor",
    padding = "padding",
    content = "content",
    customCSS = "customCSS",
    // Valtech Custom Code
    showWrapper = "showWrapper",
}

/* Valtech Custom Code - Start */
const paddingField = getCssUnitField(fields.padding, { shorthands: ["top", "right", "bottom", "left"] });
/* Valtech Custom Code - End */

interface Props extends WidgetProps {
    fields: {
        [fields.backgroundColor]: string;
        [fields.padding]: string; // Valtech Custom Code
        [fields.content]: string;
        [fields.customCSS]: string;
        // Valtech Custom Code
        [fields.showWrapper]: boolean;
    };
}

export interface RichContentStyles {
    wrapper?: InjectableCss;
}

const richContentStyles = {
    /* Store Custom Code - Start */
    wrapper: {
        css: css`
            ${spacingStyles(pxToRem(32), pxToRem(32))}
            h1,
            h2,
            h3,
            h4,
            h5,
            h6,
            blockquote {
                color: ${getColor("primary")};
            }
            blockquote {
                font-size: 1.3rem;
                text-align: center;
            }
        `,
    },
    /* Store Custom Code - End */
};

/* Valtech Custom Code */
const defaultCustomCss = `& {
}`;
/* Valtech Custom Code */

export const RichContent: React.FunctionComponent<Props> = props => {
    if (!props.fields.content) {
        return null;
    }

    const styles = useMergeStyles("richContent", richContentStyles);

    /* Valtech Custom Code - Start */
    const { backgroundColor, content, padding, showWrapper } = props.fields;

    const parsedContent = parse(content, parserOptions);

    const _showWrapper = typeof showWrapper === "boolean" ? showWrapper : backgroundColor || padding; // Preserve legacy wrapper behavior
    // Store Removed - return
    /* Valtech Custom Code - End */

    const customCssWrapper = {
        css: css`
            ${styles.wrapper?.css /* Valtech Custom Code */}
            ${props.fields.customCSS}
        `,
    };

    return (
        /* Valtech - Removed StyledWrapper */
        <ContentWrapper
            className="wrapper"
            {...customCssWrapper} // Valtech Custom Code
            backgroundColor={props.fields.backgroundColor}
            padding={paddingField.parse(padding)} // Valtech Custom Code
        >
            {parsedContent /* Valtech Custom Code */}
        </ContentWrapper>
    );
};

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: RichContent,
    definition: {
        group: "Basic",
        icon: "text",
        fieldDefinitions: [
            /* Valtech Custom Code - Start */
            {
                name: fields.showWrapper,
                displayName: "Show Wrapper",
                editorTemplate: "CheckboxField",
                fieldType: "General",
                defaultValue: null as any,
                tab: basicTab,
            },
            /* Valtech Custom Code - End */
            {
                fieldType: "General",
                name: fields.backgroundColor,
                editorTemplate: "ColorPickerField",
                displayName: "Background Color",
                defaultValue: "",
                tab: basicTab,
                isVisible: x => x.fields.showWrapper, // Valtech Custom Code
            },
            {
                ...paddingField.definition, // Valtech Custom Code
                fieldType: "General",
                // Valtech Removed - name
                // Valtech Removed - editorTemplate
                displayName: "Content Padding",
                defaultValue: "", // Valtech Custom Code
                tab: basicTab,
                isVisible: x => x.fields.showWrapper, // Valtech Custom Code
            },
            {
                name: fields.content,
                displayName: "Content",
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "Contextual",
                validate: (html: string) => {
                    const arrayOfStyles = extractStylesToArray(html);
                    return arrayOfStyles?.every(style => isSafe(style))
                        ? null
                        : "The HTML contains invalid styles that prevent it from being saved";
                },
                tab: basicTab,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) =>
                    !!shouldDisplayAdvancedFeatures && item.fields.showWrapper, // Valtech Custom Code
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;

export const ContentWrapper = styled.div<
    { backgroundColor: string; padding?: BreakpointValue[] } & InjectableCss /* Valtech Custom Code */
>`
    ${({ backgroundColor }) => (backgroundColor ? `background-color: ${backgroundColor};` : null)};
    ${({ padding }) => breakpointValues(padding, "padding") /* Valtech Custom Code */};
    ${widgetWrapperStyles.css /* Valtech Custom Code */}
    ${injectCss}
`;
